

import { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
// import { useGSAP } from '@gsap/react';
import BackgroundVisuals from "../components/BackgroundVisuals";

gsap.registerPlugin(ScrollTrigger);

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const headerRef = useRef(null);
  

    const serviceData = {
    'ai-consultation-advisory': {
      title: 'AI Consultation & Advisory',
      subtitle: 'Strategic AI Guidance for Business Transformation',
      description: 'Strategic AI guidance that transforms business challenges into competitive advantages through comprehensive assessment and data-driven roadmaps.',
      overview: 'Our AI Consultation & Advisory service provides organizations with strategic direction to successfully implement AI solutions. We conduct comprehensive assessments of your current capabilities, identify high-impact opportunities, and create detailed roadmaps for AI transformation.',
      phases: [
        {
          title: 'Assessment & Discovery',
          description: 'Comprehensive evaluation of current state, capabilities, and opportunities.',
          deliverables: ['Current State Analysis', 'AI Readiness Assessment', 'Opportunity Identification', 'Stakeholder Interviews']
        },
        {
          title: 'Strategy Development',
          description: 'Development of comprehensive AI strategy and implementation roadmap.',
          deliverables: ['AI Strategy Document', 'Implementation Roadmap', 'ROI Analysis', 'Risk Assessment']
        },
        {
          title: 'Planning & Recommendations',
          description: 'Detailed planning and actionable recommendations for implementation.',
          deliverables: ['Detailed Project Plans', 'Resource Requirements', 'Technology Recommendations', 'Success Metrics']
        }
      ],
      benefits: [
        'Clear AI strategy aligned with business objectives',
        'Identification of high-ROI AI opportunities',
        'Risk mitigation and compliance guidance',
        'Technology stack recommendations',
        'Implementation roadmap with timelines',
        'ROI projections and success metrics'
      ],
      technologies: ['Machine Learning Platforms', 'Cloud Services (AWS, Azure, GCP)', 'Data Analytics Tools', 'AI/ML Frameworks'],
      targetIndustries: ['Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Technology', 'Energy']
    },
    'cybersecurity-services': {
      title: 'Cybersecurity Services',
      subtitle: 'AI-Powered Security Operations',
      description: 'AI-powered security operations that detect, respond, and prevent threats faster than traditional human-only approaches.',
      overview: 'Our Cybersecurity Services leverage artificial intelligence to enhance your security posture, automate threat detection, and respond to incidents faster than traditional methods. We integrate AI engines with your existing security infrastructure.',
      phases: [
        {
          title: 'Security Assessment',
          description: 'Comprehensive security audit and vulnerability assessment.',
          deliverables: ['Security Audit Report', 'Vulnerability Assessment', 'Risk Analysis', 'Compliance Review']
        },
        {
          title: 'AI Integration',
          description: 'Integration of AI-powered security tools and systems.',
          deliverables: ['AI Security Engine', 'SIEM Integration', 'Automated Response Systems', 'Threat Intelligence']
        },
        {
          title: 'Monitoring & Optimization',
          description: 'Implementation of continuous monitoring and system optimization.',
          deliverables: ['Monitoring Dashboard', 'Alert Systems', 'Performance Reports', 'Optimization Recommendations']
        }
      ],
      benefits: [
        '99.9% threat detection accuracy',
        'Automated incident response',
        'Real-time threat intelligence',
        'Reduced false positive rates',
        '24/7 security monitoring',
        'Compliance automation'
      ],
      technologies: ['AI Security Platforms', 'SIEM Systems', 'Threat Intelligence Tools', 'Behavioral Analytics'],
      targetIndustries: ['Finance', 'Healthcare', 'Government', 'Technology', 'Energy', 'Retail']
    },
    'ai-automation-digital-transformation': {
      title: 'AI Automation & Digital Transformation',
      subtitle: 'Intelligent Process Automation',
      description: 'Modern AI/MLOps implementations that identify automation opportunities and transform complex, fragmented business processes.',
      overview: 'Transform your business operations with intelligent automation that goes beyond traditional RPA. We implement AI-driven solutions that can handle complex, unstructured tasks and continuously improve performance.',
      phases: [
        {
          title: 'Process Analysis',
          description: 'Deep analysis of existing processes and automation opportunities.',
          deliverables: ['Process Maps', 'Automation Opportunities', 'ROI Analysis', 'Implementation Plan']
        },
        {
          title: 'AI Development',
          description: 'Development of AI-powered automation solutions.',
          deliverables: ['AI Models', 'Automation Workflows', 'Integration APIs', 'Testing Results']
        },
        {
          title: 'Deployment & Optimization',
          description: 'Production deployment and performance optimization.',
          deliverables: ['Production System', 'Monitoring Tools', 'Performance Reports', 'User Training']
        }
      ],
      benefits: [
        'Up to 80% reduction in manual tasks',
        'Improved accuracy and consistency',
        'Faster processing times',
        'Cost savings through automation',
        'Scalable intelligent workflows',
        'Continuous process improvement'
      ],
      technologies: ['RPA Platforms', 'Machine Learning', 'Process Mining Tools', 'Workflow Automation'],
      targetIndustries: ['Manufacturing', 'Finance', 'Healthcare', 'Retail', 'Logistics', 'Insurance']
    },
    'connect-collaborate-co-develop': {
      title: 'Connect, Collaborate & Co-develop',
      subtitle: 'End-to-End Product Development',
      description: 'End-to-end product development from concept to enterprise-grade architecture using agile methodologies and AI/MLOps practices.',
      overview: 'Partner with us for comprehensive product development that combines strategic planning with hands-on execution. We work collaboratively to build scalable, production-ready solutions using modern development practices.',
      phases: [
        {
          title: 'Discovery & Planning',
          description: 'Comprehensive product discovery and development planning.',
          deliverables: ['Product Requirements', 'Technical Architecture', 'Development Roadmap', 'Resource Plan']
        },
        {
          title: 'MVP Development',
          description: 'Rapid development of minimum viable product.',
          deliverables: ['MVP Product', 'Core Features', 'Testing Suite', 'Documentation']
        },
        {
          title: 'Scaling & Enterprise',
          description: 'Scaling to enterprise-grade production system.',
          deliverables: ['Production System', 'Scalability Features', 'Security Implementation', 'Deployment Pipeline']
        }
      ],
      benefits: [
        'Faster time to market',
        'Scalable architecture design',
        'Modern development practices',
        'Continuous integration/deployment',
        'Quality assurance and testing',
        'Knowledge transfer and training'
      ],
      technologies: ['Cloud Platforms', 'Modern Frameworks', 'DevOps Tools', 'AI/ML Platforms'],
      targetIndustries: ['Technology', 'Startups', 'Healthcare', 'Finance', 'E-commerce', 'SaaS']
    },
    'startup-growth-acceleration': {
      title: 'Startup Growth & Acceleration',
      subtitle: 'Comprehensive Tech Partnership',
      description: 'Comprehensive tech partnership combining strategic advisory and hands-on execution to build scalable, cloud-native architectures with embedded AI.',
      overview: 'Act as your dedicated technology partner, providing both strategic guidance and hands-on development to accelerate your startup growth. We help you build scalable systems while embedding AI capabilities from day one.',
      phases: [
        {
          title: 'Strategic Assessment',
          description: 'Comprehensive assessment of technology needs and growth strategy.',
          deliverables: ['Technology Assessment', 'Growth Strategy', 'Architecture Plan', 'Investment Roadmap']
        },
        {
          title: 'Foundation Building',
          description: 'Building scalable technology foundation and core systems.',
          deliverables: ['Core Platform', 'Scalable Architecture', 'AI Integration', 'Security Framework']
        },
        {
          title: 'Growth & Optimization',
          description: 'Optimization for scale and continuous improvement.',
          deliverables: ['Performance Optimization', 'Advanced Features', 'Analytics Platform', 'Growth Tools']
        }
      ],
      benefits: [
        'Accelerated product development',
        'Scalable cloud-native architecture',
        'Embedded AI capabilities',
        'Investment readiness',
        'Technical team augmentation',
        'Strategic technology guidance'
      ],
      technologies: ['Cloud Platforms', 'Microservices', 'AI/ML Services', 'Analytics Tools'],
      targetIndustries: ['Technology Startups', 'SaaS', 'E-commerce', 'FinTech', 'HealthTech', 'EdTech']
    },
    'data-intelligence-analytics-platform': {
      title: 'Data Intelligence & Analytics Platform',
      subtitle: 'Transform Data into Actionable Insights',
      description: 'Transform raw data into actionable business intelligence through advanced analytics platforms, predictive modeling, and real-time decision support systems.',
      overview: 'Build comprehensive data intelligence platforms that turn your raw data into strategic assets. We create unified analytics systems with advanced AI models for predictive insights and real-time decision support.',
      phases: [
        {
          title: 'Data Assessment',
          description: 'Comprehensive analysis of data sources and requirements.',
          deliverables: ['Data Audit', 'Architecture Design', 'Requirements Analysis', 'Implementation Plan']
        },
        {
          title: 'Platform Development',
          description: 'Development of unified data platform and analytics systems.',
          deliverables: ['Data Platform', 'Analytics Engine', 'ML Pipelines', 'Dashboard System']
        },
        {
          title: 'Optimization & Training',
          description: 'Performance optimization and team training.',
          deliverables: ['Optimized System', 'Training Materials', 'Documentation', 'Support Plan']
        }
      ],
      benefits: [
        'Unified data lake and warehouse',
        'Real-time analytics and insights',
        'Predictive modeling capabilities',
        'Automated reporting systems',
        'Self-service analytics',
        'Data-driven decision making'
      ],
      technologies: ['Data Warehouses', 'Analytics Platforms', 'ML Frameworks', 'Visualization Tools'],
      targetIndustries: ['Retail', 'Finance', 'Healthcare', 'Manufacturing', 'Technology', 'Media']
    }
  };

  const service = serviceData[serviceId];

  if (!service) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-4xl font-black text-[#17091a] mb-4 uppercase tracking-tighter">System Error: 404</h1>
          <p className="text-gray-500 mb-8 font-medium">Service Architecture Not Found</p>
          <Link to="/services" className="px-8 py-3 bg-[#952877] text-white font-black text-xs uppercase tracking-widest rounded-xl">
            Return to Core
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#17091a] selection:bg-[#952877]/10 selection:text-[#952877]">
      <BackgroundVisuals />
      
      {/* --- HERO SECTION --- */}
      <section ref={headerRef} className="pt-40 pb-24 relative z-10 px-6 overflow-hidden">
        {/* Decorative Brand Glows (Adjusted for Light Theme) */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#952877]/5 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#592aa7]/5 blur-[100px] rounded-full pointer-events-none"></div>
        
        <div className="max-w-6xl mx-auto text-center">
            <span className="text-xs font-black uppercase tracking-[0.5em] text-[#952877] mb-6 block">
              Specialized Infrastructure
            </span>
            <h1 className="text-5xl md:text-8xl font-black text-[#17091a] mb-8 tracking-tighter leading-[1.1]">
                {service.title.split(' ').map((word, i) => (
                  <span key={i} className={i % 2 !== 0 ? "bg-gradient-to-r from-[#952877] to-[#592aa7] bg-clip-text text-transparent px-2 italic" : ""}>
                    {word}{' '}
                  </span>
                ))}
            </h1>
            <p className="text-xl md:text-2xl text-gray-500 max-w-4xl mx-auto leading-relaxed font-light">
                {service.overview}
            </p>
        </div>
      </section>

      <div className="relative z-10 space-y-32 pb-32">
        
        {/* --- EXECUTION FRAMEWORK (Light Glass Cards) --- */}
        <section className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div className="max-w-xl">
              <h2 className="text-xs font-black uppercase tracking-[0.4em] text-[#952877] mb-4">Implementation</h2>
              <h3 className="text-4xl md:text-6xl font-black text-[#17091a] tracking-tighter">The Execution <span className="italic font-light">Matrix</span></h3>
            </div>
            <p className="text-gray-400 font-medium italic">A structured 3-phase neural deployment.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {service.phases.map((phase, index) => (
              <div 
                key={index} 
                className="phase-card group bg-white/40 backdrop-blur-xl rounded-[2.5rem] border border-gray-100 p-10 hover:border-[#952877]/30 transition-all duration-700 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.03)] hover:shadow-[0_40px_80px_-20px_rgba(149,40,119,0.1)] relative overflow-hidden"
              >
                <div className="mb-8">
                  <span className="text-[10px] font-black text-[#952877] uppercase tracking-[0.3em] bg-[#952877]/5 px-3 py-1.5 rounded-lg border border-[#952877]/10">
                    Stage 0{index + 1}
                  </span>
                </div>
                
                <h3 className="text-2xl font-black text-[#17091a] mb-4 tracking-tight">{phase.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">{phase.description}</p>
                
                <div className="h-px bg-gray-100 w-full mb-8"></div>
                
                <ul className="space-y-4">
                  {phase.deliverables.map((item, idx) => (
                    <li key={idx} className="flex items-center text-[11px] font-bold uppercase tracking-widest text-gray-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#952877] mr-4 shadow-[0_0_8px_rgba(149,40,119,0.4)]"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* --- BENEFITS (Split High-Contrast Section) --- */}
        <section className="py-32 bg-[#17091a] rounded-[4rem] text-white overflow-hidden relative">
            {/* Dark Mode Break inside Light Theme */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#952877]/10 blur-[100px] rounded-full"></div>
            
            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="grid lg:grid-cols-12 gap-20 items-center">
                    <div className="lg:col-span-5">
                        <h2 className="text-xs font-black uppercase tracking-[0.4em] text-[#952877] mb-6">Strategic Value</h2>
                        <h3 className="text-4xl md:text-6xl font-black tracking-tighter leading-none mb-10 text-white">
                          Why Partner <br/>With <span className="italic italic-text">ViconAI?</span>
                        </h3>
                        <p className="text-gray-400 text-lg leading-relaxed mb-10 font-medium">
                            We deliver measurable business impact. Our solutions are designed to scale, adapt, and provide long-term proprietary value.
                        </p>
                        <Link to="/contact" className="inline-flex h-14 items-center px-10 bg-[#952877] text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-white hover:text-[#17091a] transition-all shadow-[0_10px_20px_rgba(149,40,119,0.2)]">
                          Request Technical Brief
                        </Link>
                    </div>
                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                        {service.benefits.map((benefit, index) => (
                            <div key={index} className="p-8 rounded-[2rem] bg-white/5 border border-white/10 flex flex-col justify-between hover:bg-white/10 transition-all group">
                                <div className="w-10 h-10 rounded-xl bg-[#952877] flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                </div>
                                <span className="text-white font-black text-sm uppercase tracking-tight">{benefit}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>

        {/* --- TECH & INDUSTRIES (Light Dashboard Style) --- */}
        <section className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-10">
            {/* Tech Stack */}
            <div className="bg-[#fafafa] border border-gray-100 rounded-[3rem] p-12 shadow-sm">
              <h3 className="text-xl font-black uppercase tracking-[0.3em] text-[#952877] mb-12 flex items-center">
                <span className="w-8 h-px bg-[#952877] mr-4"></span>
                Technology Stack
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {service.technologies.map((tech, index) => (
                  <div key={index} className="group p-5 rounded-2xl bg-white border border-gray-100 hover:border-[#952877]/30 transition-all">
                    <span className="text-gray-300 group-hover:text-[#952877] font-mono text-[10px] block mb-1 uppercase tracking-tighter">STK_{index+1}</span>
                    <span className="text-[#17091a] font-black text-sm uppercase tracking-tight">{tech}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Industries */}
            <div className="bg-white border border-gray-100 rounded-[3rem] p-12 relative overflow-hidden shadow-sm">
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#592aa7]/5 blur-[80px] rounded-full"></div>
              <h3 className="text-xl font-black uppercase tracking-[0.3em] text-[#17091a] mb-12 flex items-center">
                <span className="w-8 h-px bg-gray-200 mr-4"></span>
                Sectors Served
              </h3>
              <div className="flex flex-wrap gap-3 relative z-10">
                {service.targetIndustries.map((industry, index) => (
                  <span key={index} className="px-6 py-3 rounded-full bg-gray-50 border border-gray-100 text-gray-500 font-bold text-[10px] uppercase tracking-widest hover:bg-[#17091a] hover:text-white transition-all cursor-default">
                    {industry}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* --- FINAL CTA --- */}
        <section className="max-w-7xl mx-auto px-6">
          <div className="bg-[#17091a] rounded-[4rem] p-12 md:p-24 text-center border border-gray-100 relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(149,40,119,0.15),transparent_70%)]"></div>
            <div className="relative z-10 max-w-3xl mx-auto">
              <h2 className="text-4xl md:text-7xl font-black mb-10 tracking-tighter leading-tight text-white">
                Ready to <span className="text-[#952877] italic">Optimize?</span>
              </h2>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link to="/contact" className="px-12 py-6 bg-[#952877] text-white font-black text-xs uppercase tracking-[0.3em] rounded-2xl shadow-[0_20px_40px_rgba(149,40,119,0.3)] hover:-translate-y-2 transition-all duration-500">
                  Initialize Project
                </Link>
                <Link to="/" className="px-12 py-6 border border-white/10 text-white font-black text-xs uppercase tracking-[0.3em] rounded-2xl hover:bg-white/10 transition-all">
                  Back to System
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServiceDetailPage;



